import React, { useEffect, useState, useCallback } from 'react';
import Wrapper from '../../components/Wrapper';
import { Card, Col, Row, Button, Form, Accordion, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchFarmerDetails } from '../../store/features/farmerProfileSlice';
import axiosInstance from '../../utils/axios';
import { getRole } from '../../utils/role';
import GoogleMap from '../Field_Officer/GoogleMap';
import { createFarmTranslations } from '../../ui/src/translations';

const sizeUnits = [
  'sq mtr', 'Acres', 'Hectare', 'Bigha', 'Gaj', 'Kanal', 'Biswa', 'Dismil', 'Killa',
  'Marla', 'Nali', 'Guntha', 'Square Karam', 'Ankanam', 'Gajam', 'Chatak',
  'Dhur', 'Perch', 'Lessa', 'Murabba', 'Biswa Kacha', 'Pura'
];

const conversionRates = {
  'sq mtr': 1,
  'Acres': 4046.8564,
  'Hectare': 10000,
  'Bigha': 2529.28,
  'Gaj': 0.836127,
  'Kanal': 505.857,
  'Biswa': 126.464,
  'Dismil': 40.468,
  'Killa': 4046.8564,
  'Marla': 25.2928,
  'Nali': 200,
  'Guntha': 101.171,
  'Square Karam': 2.529,
  'Ankanam': 6.689,
  'Gajam': 0.836127,
  'Chatak': 4.18,
  'Dhur': 6.323,
  'Perch': 25.2928,
  'Lessa': 14.4,
  'Murabba': 101171.41,
  'Biswa Kacha': 42.15,
  'Pura': 4000
};

function CreateFarm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { status, farmerProfile } = useSelector((state) => state.farmerProfile);

  const [farmName, setFarmName] = useState('');
  const [farmSize, setFarmSize] = useState('');
  const [farmSizeUnit, setFarmSizeUnit] = useState('Acres'); 
  const [geoFence, setGeoFence] = useState([]);
  const [tempLat, setTempLat] = useState(20.5937);
  const [tempLon, setTempLon] = useState(78.9629);
  const [fetchingLoc, setFetchingLoc] = useState(false);
  const [locFetched, setLocFetched] = useState(false);
  const [language, setLanguage] = useState("en");

  const t = (key) =>
    createFarmTranslations[language]?.[key] ??
    createFarmTranslations.en[key] ??
    key;

  const getLocSuccess = useCallback((position) => {
    setTempLat(position.coords.latitude);
    setTempLon(position.coords.longitude);
    setFetchingLoc(false);
    setLocFetched(true);
  }, []);

  const getLocError = useCallback(() => {
    setFetchingLoc(false);
    alert("Unable to retrieve your location");
    setLocFetched(true);
  }, []);

  const handleFetchLocation = useCallback(() => {
    if (fetchingLoc) return;

    if (navigator.geolocation) {
      setFetchingLoc(true);
      navigator.geolocation.getCurrentPosition(getLocSuccess, getLocError);
    } else {
      alert("Geolocation not supported");
    }
  }, [fetchingLoc, getLocSuccess, getLocError]); 

  const callCreateFarmApi = useCallback(async () => {
    try {
      const role = getRole();
      const payload = {
        farm_name: farmName,
        farm_size: farmSize,
        farm_size_unit: farmSizeUnit,
        geo_location: { data: geoFence },
        owned_by: id,
      };

      let endpoint = '/field-officer/create-farm';
      if ((role === 'PARTNER' || role === 'TEAM_MEMBER')) endpoint = '/admin/create-farm';
      else if (role === 'SUPPORT') endpoint = '/super-admin/create-farm';

      const { data } = await axiosInstance.post(endpoint, { ...payload });
      console.log(data);

      if (data.data.farm_id) {
        setFarmName('');
        setFarmSize('');
        setFarmSizeUnit('Acres');
        setGeoFence([]);
        setTempLat('');
        setTempLon('');

        alert('Farm created successfully');
        navigate(-1);
      }
    } catch (err) {
      alert('Failed to create farm');
      console.log(err);
    }
  }, [farmName, farmSize, farmSizeUnit, geoFence, id, navigate]); 

  const handleSubmit = useCallback(() => {
    console.log('Submitting Farm:', { farmName, farmSize, farmSizeUnit, geoFence });

    if (geoFence.length === 0) {
      alert('Please draw the farm boundary on the map before submitting.\n\nTap "Draw Farm Boundary", mark all corners of your farm, then close the polygon by clicking the starting point.');
      return;
    }

    if (geoFence.length < 3) {
      alert('The farm boundary is incomplete.\n\nA closed polygon needs at least 3 corner points. Please reset and redraw the boundary by marking all corners of your farm.');
      return;
    }

    if (!farmName || !farmSize || !farmSizeUnit) {
      alert('Please fill in all required fields (Farm Name, Size, and Unit).');
      return;
    }

    callCreateFarmApi();
  }, [farmName, farmSize, farmSizeUnit, geoFence, callCreateFarmApi]);

  useEffect(() => {
    dispatch(fetchFarmerDetails({ id }));
  }, [dispatch, id]);

  useEffect(() => {
    handleFetchLocation();
  }, [handleFetchLocation]); 

  const updateGeoFence = (fenceArr, polyAreaInSqMtr) => {
    setGeoFence(fenceArr);
    
    if (polyAreaInSqMtr > 0) {
      const rate = conversionRates[farmSizeUnit] || 1;
      const convertedSize = polyAreaInSqMtr / rate;
      setFarmSize(Number(convertedSize.toFixed(2)).toString());
    } else {
      setFarmSize('');
    }
  }

  const handleUnitChange = (e) => {
    const newUnit = e.target.value;
    const oldUnit = farmSizeUnit;
    
    if (farmSize && !isNaN(farmSize)) {
      const sizeInSqMeters = parseFloat(farmSize) * (conversionRates[oldUnit] || 1);
      
      const convertedSize = sizeInSqMeters / (conversionRates[newUnit] || 1);
      
      setFarmSize(Number(convertedSize.toFixed(2)).toString());
    }
    
    setFarmSizeUnit(newUnit);
  };

  return (
    <Wrapper>
      <div className="d-flex justify-content-end mb-3">
        <ButtonGroup size="sm">
          <ToggleButton
            id="farm-lang-en"
            type="radio"
            variant={language === "en" ? "success" : "outline-success"}
            checked={language === "en"}
            value="en"
            onChange={() => setLanguage("en")}
          >
            {t("english")}
          </ToggleButton>

          <ToggleButton
            id="farm-lang-hi"
            type="radio"
            variant={language === "hi" ? "success" : "outline-success"}
            checked={language === "hi"}
            value="hi"
            onChange={() => setLanguage("hi")}
          >
            {t("hindi")}
          </ToggleButton>
        </ButtonGroup>
      </div>
      <h1 className='pt-4'>
        {t("createFarmFor")}{" "}
        {status === 'loading' ? (
          <span>Loading...</span>
        ) : (
          <span className='fw-bold'>
            {farmerProfile?.first_name} {farmerProfile?.last_name}
          </span>
        )}
      </h1>
      <Row className='g-3'>
        <Col lg={8} className='r-5 order-2 order-lg-1'>
          <Card>
            <h4>{t("geoFencing")}</h4>
            <span><i>{t("allowLocation")}</i></span>

            <br />
            {locFetched && <GoogleMap mapCenter={{ 'lat': tempLat, 'lng': tempLon }} setGeoFence={updateGeoFence} />}
            {locFetched && geoFence.length === 0 && (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 14px',
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: 6,
                  color: '#856404',
                  fontSize: 14,
                }}
              >
                <strong>{t("boundaryNotDrawn")}.</strong> {t("boundaryNotDrawnDesc")}
              </div>
            )}

            {geoFence.length > 0 && geoFence.length < 3 && (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 14px',
                  background: '#f8d7da',
                  border: '1px solid #f5c6cb',
                  borderRadius: 6,
                  color: '#842029',
                  fontSize: 14,
                }}
              >
                <strong>{t("boundaryIncomplete")}.</strong> Only {geoFence.length} point{geoFence.length > 1 ? 's' : ''} recorded — a closed polygon needs at least 3 corners. Please reset and redraw.
              </div>
            )}

            {geoFence.length >= 3 && (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 14px',
                  background: '#d1e7dd',
                  border: '1px solid #badbcc',
                  borderRadius: 6,
                  color: '#0f5132',
                  fontSize: 14,
                }}
              >
                ✓ Farm boundary captured — {geoFence.length} points.
              </div>
            )}
          </Card>

          <Card>
            <h4>{t("otherDetails")}</h4>
            <Form.Group className="mb-3">
              <Form.Label>{t("farmName")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("enterFarmName")}
                value={farmName}
                onChange={(e) => setFarmName(e.target.value)}
              />
              <Form.Text className="text-muted">
                {t("farmNameHint")}
              </Form.Text>
            </Form.Group>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>{t("farmSize")}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t("farmSizePlaceholder")}
                    value={farmSize}
                    onChange={(e) => setFarmSize(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>{t("farmSizeUnit")}</Form.Label>
                  <Form.Select
                    aria-label="Default select example"
                    value={farmSizeUnit}
                    onChange={handleUnitChange} 
                  >
                    {sizeUnits.map((unit, index) => (
                      <option key={index} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card>

          <Card>
            <Row>
              <Col md={6}
                style={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Button variant="warning"
                  style={{ width: '90%' }}
                  onClick={() => window.location.reload()}
                >
                  {t("reset")}
                </Button>
              </Col>

              <Col md={6}
                style={{
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Button variant="success"
                  onClick={handleSubmit}
                  style={{ width: '90%' }}
                >
                  {t("submit")}
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col className='order-1 order-lg-2'>
          <div className='card-wrapper'>
            <Card>
              <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <strong>{t("instructions")}</strong>
                  </Accordion.Header>

                  <Accordion.Body>
                    <ol className="mb-0 ps-3">
                      <li className="mb-2">
                        {t("instruction1")}
                      </li>

                      <li className="mb-2">
                        {t("instruction2")}
                      </li>

                      <li className="mb-2">
                        {t("instruction3")}
                      </li>

                      <li className="mb-2">
                        {t("instruction4")}
                      </li>

                      <li className="mb-2">
                        {t("instruction5")}
                      </li>

                      <li className="mb-2">
                        {t("instruction6")}
                      </li>

                      <li>
                        {t("instruction7")}
                      </li>
                    </ol>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Card>
          </div>
        </Col>
      </Row>
    </Wrapper>
  );
}

export default CreateFarm;